import React, { useEffect, useState } from 'react';

import { acquirePlanningLoadingClock, readPlanningLoadingProgress } from './planningLoadingProgress';

/** Bar repaint cadence; the CSS transition on the fill smooths between ticks. */
export const LOADING_BAR_TICK_MS = 100;
/** Keep synthetic progress moving while a real stage floor is waiting to settle. */
export const LOADING_BAR_MIN_STEP = 0.35;
/** Leave the final two percent for the gate's real completion signal. */
export const LOADING_BAR_HOLD_CEILING = 98;

type PlanningLoadingBarProps = {
  /** Lower bound supplied by whoever knows real progress (the workspace gate). */
  floor?: number;
  /** Snap to 100% (the gate is releasing). */
  complete?: boolean;
};

const useTickingProgress = (floor: number): number => {
  const [value, setValue] = useState(() => readPlanningLoadingProgress(floor));

  useEffect(() => acquirePlanningLoadingClock(), []);

  useEffect(() => {
    const timer = setInterval(() => {
      setValue((current) => readPlanningLoadingProgress(Math.max(
        floor,
        Math.min(LOADING_BAR_HOLD_CEILING, current + LOADING_BAR_MIN_STEP),
      )));
    }, LOADING_BAR_TICK_MS);

    return () => clearInterval(timer);
  }, [floor]);

  return Math.max(value, floor);
};

/**
 * The very thin line under "Loading workspace…". Trickles on its own from a
 * shared clock so the preboot, Suspense, auth and gate shells read as one
 * continuous bar; the gate raises `floor` as real signals settle.
 */
export const PlanningLoadingBar: React.FC<PlanningLoadingBarProps> = ({ floor = 0, complete = false }) => {
  const progress = useTickingProgress(complete ? 100 : floor);
  const rounded = Math.round(progress);

  return (
    <span
      className="planning-route-shell__progress"
      role="progressbar"
      aria-label="Workspace loading progress"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={rounded}
    >
      <span
        className="planning-route-shell__progress-fill"
        style={{ transform: `scaleX(${(progress / 100).toFixed(4)})` }}
      />
    </span>
  );
};
